'use client';

import React from 'react';
import {
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  useRoomContext,
  useTrackToggle,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useRaiseHand } from './useRaiseHand';
import {
  isShortcutAllowed,
  matchShortcut,
  SCREEN_SHARE_CAPTURE_OPTIONS,
  type ShortcutAction,
} from './shortcuts';

function handleShortcutError(error: unknown) {
  if (
    error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
  ) {
    return;
  }
  console.error(error);
}

export function KeyboardShortcuts() {
  const room = useRoomContext();
  const permissions = useLocalParticipantPermissions();
  const { toggle: toggleMic } = useTrackToggle({ source: Track.Source.Microphone });
  const { toggle: toggleCamera } = useTrackToggle({ source: Track.Source.Camera });
  const { toggle: toggleScreenShare } = useTrackToggle({
    source: Track.Source.ScreenShare,
    captureOptions: SCREEN_SHARE_CAPTURE_OPTIONS,
  });
  const { toggleRaiseHand } = useRaiseHand();
  const layoutContext = useMaybeLayoutContext();

  React.useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      const action = matchShortcut(event);
      const browserSupportsScreenSharing =
        typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
      if (
        !action ||
        !isShortcutAllowed(action, permissions) ||
        (action === 'screenShare' && !browserSupportsScreenSharing)
      ) {
        return;
      }
      event.preventDefault();

      const actions: Record<ShortcutAction, () => void | Promise<unknown>> = {
        microphone: () => toggleMic().catch(handleShortcutError),
        camera: () => toggleCamera().catch(handleShortcutError),
        raiseHand: () => toggleRaiseHand().catch(handleShortcutError),
        chat: () => layoutContext?.widget.dispatch?.({ msg: 'toggle_chat' }),
        screenShare: () => toggleScreenShare().catch(handleShortcutError),
        leave: () => room.disconnect().catch(handleShortcutError),
      };
      actions[action]();
    }

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [
    layoutContext,
    permissions,
    room,
    toggleCamera,
    toggleMic,
    toggleScreenShare,
    toggleRaiseHand,
  ]);

  return null;
}
